/**
 * "Seu link" (UI enxuta de 17/10/2026): copia o link, envia a mensagem pronta por WhatsApp,
 * LinkedIn ou email (a mesma mensagem nos três; "Editar mensagem" abre o texto) e mostra quantas
 * pessoas abriram e contrataram. O link é o checkout da marca com `#identificador` (o checkout
 * grava o identificador num cookie de 30 dias e aplica o desconto).
 *
 * Movimento (23/10/2026): copiar troca no lugar (.ds-troca, 140 ms) o ícone pelo ✓ e "Copiar" por
 * "Copiado", sem mudar a largura do botão; volta sozinho em 2 s (useCopied). O leitor de tela
 * ouve "Link copiado." numa região de status.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { emitActivity } from '../../lib/activity';
import { referralLink, sendMessage, useCopied, type Channel } from './referralShare';
import { useReferralText, type ReferralKey } from './referralText';

const WHATSAPP_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';
const LINKEDIN_PATH =
  'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z';

function ChannelIcon({ channel }: { channel: Channel }): JSX.Element {
  if (channel === 'email')
    return (
      <span className="material-symbols-rounded" aria-hidden="true">
        mail
      </span>
    );
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={channel === 'whatsapp' ? WHATSAPP_PATH : LINKEDIN_PATH} />
    </svg>
  );
}

interface LinkCardProps {
  slug: string;
  source: string;
  product: string;
  discount: number;
  /** Números do link; sem eles (erro ao carregar) a linha não aparece. */
  stats?: { access: number; purchases: number };
}

const CHANNELS: Channel[] = ['whatsapp', 'linkedin', 'email'];

export function LinkCard({ slug, source, product, discount, stats }: LinkCardProps): JSX.Element {
  const { t } = useTranslation();
  const { tx, number } = useReferralText();
  const link = referralLink(slug, source);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState<string | null>(null);
  const [copied, copy] = useCopied();
  const linkCopied = copied === 'link';

  const message = edited ?? tx('msg_whatsapp', { product, discount, link: link.url });
  const opened = stats
    ? tx(stats.access === 1 ? 'opened_one' : 'opened_other', { n: number(stats.access) })
    : '';
  const bought = stats
    ? tx(stats.purchases === 1 ? 'bought_one' : 'bought_other', { n: number(stats.purchases) })
    : '';

  return (
    <section className="rf-share" aria-label={tx('link_label')}>
      <span className="rf-label">{tx('link_label')}</span>
      <div className="rf-linkbox">
        <input
          type="text"
          readOnly
          aria-label={tx('link_aria')}
          value={link.display}
          onFocus={(e) => e.target.select()}
        />
        {/* Tinta: o amarelo da tela é o "Capturar Leads" da barra (guia/06-botoes). */}
        <button
          type="button"
          className="rf-btn rf-btn--dark ds-resposta"
          onClick={() => {
            emitActivity('referral.link_copied');
            copy('link', link.url);
          }}
        >
          <span className="ds-troca" aria-hidden="true">
            <span className={`material-symbols-rounded${linkCopied ? ' ds-fora' : ''}`}>
              content_copy
            </span>
            <span className={`material-symbols-rounded${linkCopied ? '' : ' ds-fora'}`}>check</span>
          </span>
          <span className="ds-troca ds-troca-texto">
            <span className={linkCopied ? 'ds-fora' : undefined} aria-hidden={linkCopied || undefined}>
              {tx('copy_link')}
            </span>
            <span className={linkCopied ? undefined : 'ds-fora'} aria-hidden={!linkCopied || undefined}>
              {tx('copied')}
            </span>
          </span>
        </button>
        <span className="rf-sr" role="status">
          {linkCopied ? t('share_copied', 'Link copiado.') : ''}
        </span>
      </div>

      <div className="rf-channels" role="group" aria-label={tx('channels')}>
        {CHANNELS.map((c) => (
          <button
            key={c}
            type="button"
            className="rf-btn rf-btn--soft ds-resposta"
            onClick={() => {
              emitActivity('referral.link_shared', { channel: c });
              sendMessage(c, message, tx('msg_email_subject'));
            }}
          >
            <ChannelIcon channel={c} />
            {tx(`channel_${c}` as ReferralKey)}
          </button>
        ))}
      </div>

      {editing ? (
        <textarea
          className="ds-campo ds-aparece"
          aria-label={tx('message_label')}
          rows={4}
          value={message}
          onChange={(e) => setEdited(e.target.value)}
        />
      ) : null}

      <div className="rf-share__meta">
        <button type="button" className="rf-link ds-resposta" onClick={() => setEditing((v) => !v)}>
          {editing ? tx('done_editing') : tx('edit_message')}
        </button>
        {stats ? (
          <>
            <span aria-hidden="true">·</span>
            <span>{`${opened}, ${bought}`}</span>
          </>
        ) : null}
      </div>
    </section>
  );
}
